import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { browserTimezone, getAgenda, knownTimezone } from "../../services/agenda";
import { Note } from "./styles";

/** Brazil's four, first in the field (in the order most people live in them). */
export const BRAZIL_TIMEZONES = ["America/Sao_Paulo", "America/Manaus", "America/Rio_Branco", "America/Noronha"];

/** Every timezone the browser knows ("America/New_York"…); just the agenda's when it knows none. */
export const allTimezones = (): string[] =>
  (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];

/** Today in a timezone, YYYY-MM-DD (in this browser's when there's none or it isn't one). */
export const todayIn = (timezone?: string) => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }
};

/** "UTC−3", "UTC+5:30": the offset now (summer time included). */
export const offsetOf = (timezone: string) => {
  try {
    const name =
      new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "shortOffset" })
        .formatToParts(new Date())
        .find(part => part.type === "timeZoneName")?.value ?? "GMT";
    return name.replace("GMT", "UTC").replace("-", "−").replace(/^UTC$/, "UTC+0");
  } catch {
    return "";
  }
};

/** "Horário de Brasília", "Eastern Time": the timezone's name in the language. */
export const zoneName = (timezone: string, lang: string) => {
  try {
    return (
      new Intl.DateTimeFormat(lang, { timeZone: timezone, timeZoneName: "longGeneric" })
        .formatToParts(new Date())
        .find(part => part.type === "timeZoneName")?.value ?? timezone
    );
  } catch {
    return timezone;
  }
};

/** "New York (UTC−4)": a timezone in the field's list. */
export const cityLabel = (timezone: string) =>
  `${(timezone.split("/").pop() ?? timezone).replace(/_/g, " ")} (${offsetOf(timezone)})`;

/** The agenda's timezone (what the editor read last, or read now); undefined until known. */
export const useAgendaTimezone = (siteId: string) => {
  const [timezone, setTimezone] = useState(() => knownTimezone(siteId));
  useEffect(() => {
    if (timezone || !siteId) return;
    let current = true;
    getAgenda(siteId)
      .then(settings => current && setTimezone(settings.timezone))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [siteId, timezone]);
  return timezone;
};

/** This computer is in another timezone than the agenda's: its hours follow the agenda's. */
export const TimezoneNotice = ({ timezone }: { timezone?: string }) => {
  const { t, i18n } = useTranslation();
  if (!timezone || offsetOf(timezone) === offsetOf(browserTimezone())) return null;
  return (
    <Note role="status">
      {t("pages.agenda.settings.timezone.otherComputer", {
        zone: `${zoneName(timezone, i18n.language)} (${offsetOf(timezone)})`,
      })}
    </Note>
  );
};
